import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { Colors, fonts, ContainerMarginSections, ScriptAccent } from '../style/GlobalStyles';
import { CTAButtonSolid } from './buttonFill';
import { CTAButtonOutline } from './buttonOut';

const Wrap = styled.div`
  padding-top: 1rem;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: ${fonts.body};
  font-size: 0.9rem;
  color: ${Colors.ink};
  padding-bottom: 1.2rem;
  border-bottom: 1px solid ${Colors.line};

  @media (max-width: 680px) {
    flex-direction: column;
    gap: 0.4rem;
    align-items: flex-start;
  }
`;

const HeroGrid = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 3rem;
  margin-top: 3rem;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: stretch;
    gap: 2rem;
  }
`;

const Left = styled.div`
  flex: 1.5;
`;

const Title = styled.h1`
  font-family: ${fonts.display};
  font-weight: 400;
  text-transform: uppercase;
  line-height: 0.9;
  margin: 0;
  color: ${Colors.ink};
  font-size: clamp(3.2rem, 11vw, 9rem);

  .dot {
    color: ${Colors.lavender};
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2.5rem;
  align-items: center;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
  }
`;

const Right = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1.2rem;
  max-width: 360px;
`;

const PhotoBlock = styled.div`
  width: 100%;
  aspect-ratio: 4 / 4.6;
  background: ${Colors.lavender};
  border-radius: 20px;
  overflow: hidden;
  position: relative;
`;

const Photo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top center;
`;

const Scribble = styled(ScriptAccent)`
  position: absolute;
  bottom: 14px;
  right: 14px;
  color: ${Colors.lime};
  text-align: right;
`;

const Name = styled.h3`
  font-family: ${fonts.body};
  font-weight: 700;
  font-size: 1.15rem;
  margin: 0;
  color: ${Colors.ink};
`;

const Role = styled.p`
  font-family: ${fonts.body};
  font-size: 0.95rem;
  color: ${Colors.muted};
  margin: 0;
  line-height: 1.55;
`;

const Socials = styled.div`
  display: flex;
  gap: 1.2rem;
  flex-wrap: wrap;

  a {
    font-family: ${fonts.body};
    font-size: 0.92rem;
    color: ${Colors.muted};
    text-decoration: none;
    transition: color 0.2s;
    &:hover {
      color: ${Colors.ink};
    }
  }
`;

const Stats = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 2rem;
  margin-top: 5rem;
  padding-top: 2.5rem;
  border-top: 1px solid ${Colors.line};

  @media (max-width: 760px) {
    flex-wrap: wrap;
    gap: 2rem 1.5rem;
  }
`;

const Stat = styled.div`
  flex: 1;
  min-width: 130px;
  /* desfase vertical escalonado tipo referencia */
  transform: translateY(${({ $offset }) => $offset || 0}px);

  @media (max-width: 760px) {
    transform: none;
  }

  & + & {
    border-left: 1px solid ${Colors.line};
    padding-left: 2rem;
  }
`;

const StatNumber = styled.div`
  font-family: ${fonts.display};
  font-size: clamp(2.4rem, 5vw, 3.6rem);
  color: ${Colors.ink};
  line-height: 1;
`;

const StatLabel = styled.div`
  font-family: ${fonts.body};
  font-size: 0.9rem;
  color: ${Colors.muted};
  margin-top: 0.8rem;
`;

const useLocalTime = () => {
  const [time, setTime] = useState('');
  useEffect(() => {
    const tick = () =>
      setTime(
        new Date().toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        })
      );
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, []);
  return time;
};

const HeroSection = ({ stats = [] }) => {
  const scope = useRef();
  const time = useLocalTime();

  useGSAP(
    () => {
      const tl = gsap.timeline();
      tl.from('.hero-title', { y: 60, opacity: 0, duration: 1, ease: 'power3.out' })
        .from('.hero-right', { x: 50, opacity: 0, duration: 0.9, ease: 'power3.out' }, '-=0.7')
        .from('.hero-stat', { y: 30, opacity: 0, duration: 0.6, stagger: 0.1, ease: 'power2.out' }, '-=0.4');
    },
    { scope }
  );

  const offsets = [0, 36, 8, 44];

  return (
    <ContainerMarginSections ref={scope}>
      <Wrap>
        <TopBar>
          <span>Medellín, CO · {time}</span>
          <span>santiagoestrada.dev@gmail.com · +57 301 521 9172</span>
        </TopBar>

        <HeroGrid>
          <Left>
            <Title className="hero-title">
              Systems<br />
              Engineer<span className="dot">.</span>
            </Title>
            <ButtonRow>
              <CTAButtonSolid onClick={() => window.open('/files/CV-SantiagoEstrada.pdf', '_blank')}>
                Download CV
              </CTAButtonSolid>
              <CTAButtonOutline
                onClick={() => window.open('https://www.linkedin.com/in/santiago-estrada-dev/', '_blank')}
              >
                Hire me →
              </CTAButtonOutline>
            </ButtonRow>
          </Left>

          <Right className="hero-right">
            <PhotoBlock>
              <Photo src="/img/profilephoto.png" alt="Santiago Estrada" />
              <Scribble>code that scales</Scribble>
            </PhotoBlock>
            <Name>Santiago Estrada</Name>
            <Role>
              I build scalable cloud solutions and intuitive interfaces, leading
              teams from concept to deployment.
            </Role>
            <Socials>
              <a href="https://www.linkedin.com/in/santiago-estrada-dev/" target="_blank" rel="noreferrer">LinkedIn</a>
              <a href="https://github.com/santiago01est" target="_blank" rel="noreferrer">GitHub</a>
              <a href="https://www.behance.net/santiagoestradaUI" target="_blank" rel="noreferrer">Behance</a>
            </Socials>
          </Right>
        </HeroGrid>

        <Stats>
          {stats.map((s, i) => (
            <Stat key={s.label} $offset={offsets[i % offsets.length]} className="hero-stat">
              <StatNumber>{s.value}</StatNumber>
              <StatLabel>{s.label}</StatLabel>
            </Stat>
          ))}
        </Stats>
      </Wrap>
    </ContainerMarginSections>
  );
};

export default HeroSection;
